import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { NECTAR_API_URL } from '@/env'
import { useFeatureFlag } from '@/featureFlags'
import { nectarApi } from './client'
import type { EnrollResponse, NectarProject, NectarTask, PaginatedResponse } from './models'

const useNectarEnabled = () => {
  const flagEnabled = useFeatureFlag('nectarMission')
  return !!NECTAR_API_URL && flagEnabled
}

export const useNectarProjects = () => {
  const enabled = useNectarEnabled()
  return useQuery({
    queryKey: ['NECTAR_PROJECTS'],
    queryFn: () => nectarApi.get<PaginatedResponse<NectarProject>>('/v1/projects', { page: 1, limit: 50 }),
    staleTime: 50_000,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    enabled,
  })
}

export const useNectarProject = (projectId: string | undefined) => {
  const enabled = useNectarEnabled()
  return useQuery({
    queryKey: ['NECTAR_PROJECT', projectId],
    queryFn: () => nectarApi.get<NectarProject>(`/v1/projects/${projectId}`),
    refetchOnWindowFocus: false,
    enabled: enabled && !!projectId,
  })
}

export const useNectarTasks = (projectId: string | undefined) => {
  const enabled = useNectarEnabled()
  return useQuery({
    queryKey: ['NECTAR_TASKS', projectId],
    queryFn: () =>
      nectarApi.get<PaginatedResponse<NectarTask>>(`/v1/projects/${projectId}/tasks`, { page: 1, limit: 50 }),
    refetchOnWindowFocus: false,
    enabled: enabled && !!projectId,
  })
}

export const useEnrollInProject = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ projectId, acceptRules }: { projectId: string; acceptRules?: boolean }) =>
      nectarApi.post<EnrollResponse>(
        `/v1/projects/${projectId}/enroll`,
        acceptRules ? { acceptRules: true } : undefined
      ),
    onSuccess: async ({ projectId }) => {
      await queryClient.refetchQueries({ queryKey: ['NECTAR_PROJECTS'] })
      await queryClient.refetchQueries({ queryKey: ['NECTAR_PROJECT', projectId] })
    },
  })
}

export const useSubmitNectarTask = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      projectId,
      taskId,
      data,
    }: {
      projectId: string
      taskId: string
      data: FormData | Record<string, unknown>
    }) => nectarApi.post(`/v1/projects/${projectId}/tasks/${taskId}/submissions`, data),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['NECTAR_TASKS', variables.projectId] }),
        queryClient.invalidateQueries({ queryKey: ['NECTAR_PROJECT', variables.projectId] }),
        queryClient.invalidateQueries({ queryKey: ['NECTAR_PROJECTS'] }),
      ])
    },
  })
}
